import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import type { ReactNode } from 'react';
import type { ArtifactSpec, Message } from '../../../flora/flora.type';
import { getAnswerMarkdown, getQuestionText, parseMarkdownBlocks } from './agent-chat-export.helpers';
import { pdfStyles } from './agent-chat-export.styles';
import type { InlineNode, MarkdownBlock } from './agent-chat-export.type';

// ─── Emoji Font Registration ─────────────────────────────────────────────────

let emojiFontRegistered = false;

/**
 * Lazily registers Twemoji as the emoji source for react-pdf.
 * Called once before PDF generation to avoid triggering the @react-pdf/font
 * import chain at module evaluation time (which breaks Linaria's CSS extraction).
 *
 * Uses the react-pdf-documented cdnjs Twemoji mirror rather than the deprecated
 * `twitter/twemoji` jsDelivr `/gh/` path. react-pdf downloads each emoji PNG via
 * `fetch` at render time, so the host must also be allow-listed in the CSP
 * `connect-src` directive (see `scripts/generate-csp.mjs`).
 *
 * @returns {void}
 */
function ensureEmojiFont(): void {
  if (emojiFontRegistered) return;
  emojiFontRegistered = true;
  Font.registerEmojiSource({
    format: 'png',
    url: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/',
  });
}

// ─── Inline Node Renderer ────────────────────────────────────────────────────

/**
 * Converts an array of parsed inline nodes into react-pdf `<Text>` elements
 * with appropriate font styling for bold, italic, bold+italic, and code spans.
 *
 * @param {InlineNode[]} nodes - Array of parsed inline nodes to render
 * @returns {ReactNode[]} Array of react-pdf Text elements
 */
function renderInlineNodes(nodes: InlineNode[]): ReactNode[] {
  return nodes.map((node, i) => {
    switch (node.type) {
      case 'bold':
        return (
          <Text key={i} style={{ fontFamily: 'Helvetica-Bold' }}>
            {node.text}
          </Text>
        );
      case 'italic':
        return (
          <Text key={i} style={{ fontFamily: 'Helvetica-Oblique' }}>
            {node.text}
          </Text>
        );
      case 'bold_italic':
        return (
          <Text key={i} style={{ fontFamily: 'Helvetica-BoldOblique' }}>
            {node.text}
          </Text>
        );
      case 'code':
        return (
          <Text key={i} style={{ fontFamily: 'Courier', backgroundColor: '#f0f0f0', fontSize: 10 }}>
            {node.text}
          </Text>
        );
      default:
        return <Text key={i}>{node.text}</Text>;
    }
  });
}

// ─── Block-Level Renderer ────────────────────────────────────────────────────

/**
 * Converts an array of parsed markdown blocks into react-pdf elements.
 * Supports headings, paragraphs, list items, code blocks, tables,
 * and horizontal rules.
 *
 * @param {MarkdownBlock[]} blocks - Array of parsed markdown blocks to render
 * @returns {ReactNode[]} Array of react-pdf View/Text elements
 */
function renderMarkdownBlocks(blocks: MarkdownBlock[]): ReactNode[] {
  return blocks.map((block, i) => {
    const key = i;
    switch (block.type) {
      case 'heading': {
        const headingStyle =
          block.level === 1 ? pdfStyles.heading1 : block.level === 2 ? pdfStyles.heading2 : pdfStyles.heading3;
        return (
          <Text key={key} style={headingStyle}>
            {renderInlineNodes(block.inline)}
          </Text>
        );
      }
      case 'paragraph':
        return (
          <Text key={key} style={pdfStyles.paragraph}>
            {renderInlineNodes(block.inline)}
          </Text>
        );
      case 'list_item':
        return (
          <View key={key} style={[pdfStyles.listRow, { paddingLeft: block.indent * 14 }]}>
            <Text style={pdfStyles.listBullet}>•</Text>
            <Text style={pdfStyles.listItemText}>{renderInlineNodes(block.inline)}</Text>
          </View>
        );
      case 'code_block':
        return (
          <Text key={key} style={pdfStyles.codeBlock}>
            {block.code}
          </Text>
        );
      case 'table': {
        const colCount = block.headers.length;
        return (
          <View key={key} style={pdfStyles.table}>
            <View style={pdfStyles.tableHeaderRow}>
              {block.headers.map((cell, ci) => (
                <Text
                  key={ci}
                  style={[
                    ci === colCount - 1 ? pdfStyles.tableHeaderCellLast : pdfStyles.tableHeaderCell,
                    { textAlign: block.alignments[ci] ?? 'left' },
                  ]}
                >
                  {renderInlineNodes(cell)}
                </Text>
              ))}
            </View>
            {block.rows.map((row, ri) => (
              <View key={ri} style={pdfStyles.tableRow}>
                {row.map((cell, ci) => (
                  <Text
                    key={ci}
                    style={[
                      ci === colCount - 1 ? pdfStyles.tableCellLast : pdfStyles.tableCell,
                      { textAlign: block.alignments[ci] ?? 'left' },
                    ]}
                  >
                    {renderInlineNodes(cell)}
                  </Text>
                ))}
              </View>
            ))}
          </View>
        );
      }
      case 'horizontal_rule':
        return <View key={key} style={pdfStyles.horizontalRule} />;
      default:
        return null;
    }
  });
}

// ─── PDF Document Builder ────────────────────────────────────────────────────

/**
 * Builds the complete react-pdf document tree for a Flora chat export.
 * Renders a header with chat name and date, followed by question/answer
 * pairs with inline artifacts, and a fixed footer on every page.
 *
 * @param {Message[]} questions - Array of user question messages
 * @param {Message[]} messages - Array of assistant response messages, paired to
 *   `questions` by shared `turnId` rather than array position — a turn with no
 *   reply (e.g. it errored before one was persisted) must not shift every
 *   later turn's answer onto the wrong question in the exported document.
 * @param {string | undefined} chatName - Optional chat name for the PDF header
 * @param {Map<string, string>} artifactImages - Map of artifact IDs to base64 PNG data URLs
 * @returns {ReactNode} Complete react-pdf Document element tree
 */
function buildPdfDocument(
  questions: Message[],
  messages: Message[],
  chatName: string | undefined,
  artifactImages: Map<string, string>,
): ReactNode {
  const formattedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const answersByTurnId = new Map<string, Message>();
  for (const message of messages) {
    if (message.turnId) answersByTurnId.set(message.turnId, message);
  }

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.headerTitle}>{chatName ?? 'Flora Chat'}</Text>
          <Text style={pdfStyles.headerDate}>Exported {formattedDate}</Text>
        </View>

        {/* Message pairs */}
        {questions.map((question, index) => {
          const answer = question.turnId ? answersByTurnId.get(question.turnId) : messages[index];
          const artifacts: ArtifactSpec[] = answer?.artifacts ?? [];
          const answerMarkdown = answer ? getAnswerMarkdown(answer) : '';
          const blocks = parseMarkdownBlocks(answerMarkdown);

          return (
            <View key={index} style={pdfStyles.messagePair}>
              {/* User message */}
              <Text style={pdfStyles.userLabel}>You</Text>
              <Text style={pdfStyles.userText}>{getQuestionText(question)}</Text>

              <View style={pdfStyles.divider} />

              {/* Assistant response */}
              {answer && (
                <View>
                  <Text style={pdfStyles.assistantLabel}>Flora</Text>
                  {renderMarkdownBlocks(blocks)}

                  {/* Inline artifacts */}
                  {artifacts.map((artifact) => {
                    const imgData = artifactImages.get(artifact.id);
                    if (!imgData) return null;
                    return (
                      <View key={artifact.id} style={pdfStyles.artifactSection}>
                        <Text style={pdfStyles.artifactLabel}>{artifact.title}</Text>
                        <Image style={pdfStyles.artifactImage} src={imgData} />
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}

        {/* Footer — fixed to bottom of every page */}
        <View style={pdfStyles.footer} fixed>
          <Image style={pdfStyles.footerLogo} src="/favicon.ico" />
          <Text style={pdfStyles.footerText}>Generated by Flora — Bloomfilter</Text>
        </View>
      </Page>
    </Document>
  );
}

export { ensureEmojiFont, buildPdfDocument };
